import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
  IconButton,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  CloudUpload as UploadIcon,
  InsertDriveFile as FileIcon,
  Close as CloseIcon,
  Visibility as PreviewIcon,
} from '@mui/icons-material';
import { uploadVehicle, uploadToFirebase, processDocument } from '../../services/api';
import { VehicleCharacteristics, RegistrationDiscrepancy, ComparisonResult, Classification } from '../../types';
import FirebaseSelector, { ReprocessResult } from '../shared/FirebaseSelector';
import DocumentPreview from '../shared/DocumentPreview';
import ProcessingStatus, { ProcessingStep } from '../ProcessingStatus';

const initialSteps: ProcessingStep[] = [
  { label: 'Document Agent', description: 'Uploading document and preparing for pipeline processing', status: 'pending' },
  { label: 'OCR Extraction Agent', description: 'Extracting text and structured fields using Document AI + Gemini', status: 'pending' },
  { label: 'ERP Comparison Agent', description: 'Matching extracted fields against ERP records from data bucket', status: 'pending' },
  { label: 'Classification Agent', description: 'Analyzing results and classifying as standard or exception case', status: 'pending' },
];

interface AnalysisResult {
  vehicle_id: string;
  characteristics: VehicleCharacteristics;
  registered_data?: Record<string, any>;
  discrepancies: RegistrationDiscrepancy[];
  status?: string;
  summary?: {
    total_discrepancies: number;
    critical_count: number;
    warning_count: number;
    info_count: number;
  };
}

function VehicleUpload() {
  const [file, setFile] = useState<File | null>(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [characteristics, setCharacteristics] = useState<VehicleCharacteristics | null>(null);
  const [discrepancies, setDiscrepancies] = useState<RegistrationDiscrepancy[]>([]);
  const [extractedData, setExtractedData] = useState<any>(null);
  const [previewedImage, setPreviewedImage] = useState<string | null>(null);
  const [comparisonResult, setComparisonResult] = useState<ComparisonResult[] | null>(null);
  const [classification, setClassification] = useState<Classification | null>(null);
  const [documentId, setDocumentId] = useState<string | null>(null);

  // Processing status state (same as Tab 1)
  const [processingSteps, setProcessingSteps] = useState<ProcessingStep[]>(initialSteps);
  const [currentStep, setCurrentStep] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isProcessingActive, setIsProcessingActive] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Timer effect
  useEffect(() => {
    if (isProcessingActive) {
      timerRef.current = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [isProcessingActive]);

  const updateStep = (stepIndex: number, status: ProcessingStep['status'], duration?: number) => {
    setProcessingSteps(prev => prev.map((step, idx) =>
      idx === stepIndex ? { ...step, status, duration } : step
    ));
    if (status === 'running') setCurrentStep(stepIndex);
  };

  const resetProcessingState = () => {
    setProcessingSteps(initialSteps.map(s => ({ ...s })));
    setCurrentStep(0);
    setElapsedTime(0);
    setIsProcessingActive(false);
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      setFile(droppedFile);
      setError(null);
      if (droppedFile.type.startsWith('image/')) {
        const url = URL.createObjectURL(droppedFile);
        setFilePreviewUrl(url);
      } else {
        setFilePreviewUrl(null);
      }
    }
  }, []);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const picked = event.target.files?.[0];
    if (picked) {
      setFile(picked);
      setError(null);
      if (picked.type.startsWith('image/')) {
        const url = URL.createObjectURL(picked);
        setFilePreviewUrl(url);
      } else {
        setFilePreviewUrl(null);
      }
    }
  };

  const handleRemoveFile = () => {
    if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
    setFile(null);
    setFilePreviewUrl(null);
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    setUploading(true);
    setError(null);
    setResult(null);
    setCharacteristics(null);
    setDiscrepancies([]);
    setComparisonResult(null);
    setClassification(null);
    resetProcessingState();
    setIsProcessingActive(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Step 1: Document Agent — Upload to Firebase
      const step1Start = Date.now();
      updateStep(0, 'running');

      console.log('[Vehicle] Calling uploadToFirebase...');
      const uploadResponse = await uploadToFirebase(formData);
      console.log('[Vehicle] Upload response:', uploadResponse);

      const step1Duration = Math.round((Date.now() - step1Start) / 1000);
      updateStep(0, 'completed', step1Duration);

      // Step 2: OCR Extraction Agent
      updateStep(1, 'running');
      const docId = uploadResponse.document_id;
      setDocumentId(docId);

      // Set extracted data from upload response
      if (uploadResponse.extracted_data) {
        const ed = uploadResponse.extracted_data;
        setExtractedData({
          ...ed,
          all_fields: ed.all_fields || ed.field_details || {},
          field_details: ed.field_details || ed.all_fields || {},
          detected_language: ed.detected_language || 'unknown',
          total_fields_extracted: ed.total_fields_extracted ||
            Object.keys(ed.all_fields || ed.field_details || {}).length
        });
      }
      await new Promise(resolve => setTimeout(resolve, 500));
      updateStep(1, 'completed', 1);

      // Step 3: ERP Comparison Agent via processDocument
      updateStep(2, 'running');
      const step3Start = Date.now();
      try {
        console.log('[Vehicle] Processing document for ERP comparison:', docId);
        const processResponse = await processDocument(docId);
        console.log('[Vehicle] Process response:', processResponse);

        if (processResponse.comparison_result) {
          setComparisonResult(processResponse.comparison_result);
        }
        if (processResponse.classification) {
          setClassification(processResponse.classification);
        }
        const step3Duration = Math.round((Date.now() - step3Start) / 1000);
        updateStep(2, 'completed', step3Duration);
      } catch (processErr) {
        console.warn('[Vehicle] ERP comparison failed (extraction still succeeded):', processErr);
        updateStep(2, 'completed', 1);
      }

      // Step 4: Classification Agent
      updateStep(3, 'running');
      await new Promise(resolve => setTimeout(resolve, 300));
      updateStep(3, 'completed', 1);

    } catch (err: any) {
      const failedStepIndex = processingSteps.findIndex(s => s.status === 'running');
      if (failedStepIndex >= 0) updateStep(failedStepIndex, 'error');
      setError(err.response?.data?.detail || err.message || 'Upload failed');
    } finally {
      setUploading(false);
      setIsProcessingActive(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'minor_issues': return 'info';
      case 'pending': return 'warning';
      case 'requires_review': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved': return <CheckIcon />;
      case 'minor_issues': return <InfoIcon />;
      case 'pending': return <WarningIcon />;
      case 'requires_review': return <ErrorIcon />;
      default: return null;
    }
  };

  return (
    <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 2, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
        🖼️ Image &amp; Multimedia Extraction
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload images or multimedia files for AI-powered content extraction and ERP comparison
      </Typography>

      {/* Image preview lightbox */}
      {previewedImage && (
        <Box
          onClick={() => setPreviewedImage(null)}
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            backgroundColor: 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'zoom-out',
          }}
        >
          <IconButton
            onClick={() => setPreviewedImage(null)}
            sx={{ position: 'absolute', top: 16, right: 16, color: '#fff', backgroundColor: alpha('#fff', 0.1) }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            component="img"
            src={previewedImage}
            alt="Preview"
            sx={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 2, boxShadow: 24 }}
          />
        </Box>
      )}

      <FirebaseSelector
        processType="vehicle"
        disabled={uploading}
        onReprocessComplete={(docId: string, result: ReprocessResult) => {
          console.log('[VehicleUpload] Reprocess complete:', docId, result);
          if (result.extracted_data) {
            const chars = result.extracted_data.characteristics || result.extracted_data;
            setCharacteristics(chars);
            setExtractedData(result.extracted_data);
            const compResults = result.comparison_result || [];
            setDiscrepancies(compResults.map((cr: any) => ({
              field: cr.field,
              severity: cr.match ? 'info' : (cr.discrepancy_type === 'missing_in_document' ? 'warning' : 'critical'),
              description: cr.match
                ? 'Values match'
                : cr.discrepancy_type === 'missing_in_document'
                  ? 'Field missing in extracted document'
                  : cr.discrepancy_type === 'missing_in_erp'
                    ? 'Field missing in ERP data'
                    : 'Mismatch detected',
              registered_value: cr.erp_value,
              extracted_value: cr.document_value,
            })));
            setResult({
              vehicle_id: docId,
              characteristics: chars,
              registered_data: result.extracted_data.registered_data,
              discrepancies: [],
              status: result.is_exception ? 'requires_review' : 'approved',
              summary: {
                total_discrepancies: compResults.filter((d: any) => !d.match).length,
                critical_count: compResults.filter((d: any) => !d.match && d.discrepancy_type === 'mismatch').length,
                warning_count: compResults.filter((d: any) => !d.match && d.discrepancy_type === 'missing_in_document').length,
                info_count: compResults.filter((d: any) => !d.match && d.discrepancy_type === 'missing_in_erp').length,
              },
            });
          }
          setError(null);
        }}
      >
        {/* Divider */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Divider sx={{ flex: 1 }} />
          <Chip
            label="Or upload new"
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', color: 'text.secondary', borderColor: 'divider' }}
          />
          <Divider sx={{ flex: 1 }} />
        </Box>

        {/* Drag & Drop Upload Area */}
        <Box
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          sx={{
            border: '2px dashed',
            borderColor: dragActive ? 'secondary.main' : file ? 'success.main' : 'divider',
            borderRadius: 3,
            p: { xs: 3, md: 4 },
            textAlign: 'center',
            background: dragActive
              ? `linear-gradient(135deg, ${alpha('#8B5CF6', 0.06)} 0%, ${alpha('#3B82F6', 0.06)} 100%)`
              : file
                ? `linear-gradient(135deg, ${alpha('#10B981', 0.04)} 0%, ${alpha('#059669', 0.04)} 100%)`
                : 'transparent',
            transition: 'all 0.25s ease',
            cursor: 'pointer',
            '&:hover': {
              borderColor: 'secondary.main',
              background: `linear-gradient(135deg, ${alpha('#8B5CF6', 0.04)} 0%, ${alpha('#3B82F6', 0.04)} 100%)`,
            },
          }}
        >
          <input
            accept="image/*,video/*,.pdf"
            style={{ display: 'none' }}
            id="multimedia-file-input"
            type="file"
            onChange={handleFileChange}
          />
          <label htmlFor="multimedia-file-input" style={{ cursor: 'pointer', display: 'block' }}>
            {file ? (
              <Box>
                {/* File preview */}
                {filePreviewUrl ? (
                  <Box
                    sx={{
                      width: 160,
                      height: 120,
                      mx: 'auto',
                      mb: 2,
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: '2px solid',
                      borderColor: 'success.main',
                      boxShadow: `0 4px 20px ${alpha('#10B981', 0.3)}`,
                      cursor: 'zoom-in',
                    }}
                    onClick={(e) => { e.preventDefault(); setPreviewedImage(filePreviewUrl); }}
                  >
                    <Box
                      component="img"
                      src={filePreviewUrl}
                      alt={file.name}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      mx: 'auto',
                      mb: 2,
                      borderRadius: 2,
                      backgroundColor: alpha('#10B981', 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <FileIcon sx={{ fontSize: 40, color: '#10B981' }} />
                  </Box>
                )}
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {file.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                  {filePreviewUrl && (
                    <Button
                      size="small"
                      startIcon={<PreviewIcon />}
                      onClick={(e) => { e.preventDefault(); setPreviewedImage(filePreviewUrl); }}
                      variant="outlined"
                      sx={{
                        textTransform: 'none',
                        borderColor: alpha('#8B5CF6', 0.5),
                        color: '#8B5CF6',
                        '&:hover': { borderColor: '#8B5CF6', backgroundColor: alpha('#8B5CF6', 0.08) },
                      }}
                    >
                      Preview
                    </Button>
                  )}
                  <Button
                    size="small"
                    startIcon={<CloseIcon />}
                    onClick={(e) => { e.preventDefault(); handleRemoveFile(); }}
                    sx={{ color: 'text.secondary', textTransform: 'none' }}
                  >
                    Remove file
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    mx: 'auto',
                    mb: 2,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #8B5CF6 0%, #3B82F6 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 4px 20px ${alpha('#8B5CF6', 0.4)}`,
                  }}
                >
                  <UploadIcon sx={{ fontSize: 36, color: '#fff' }} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Drop image or multimedia file here
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Supports JPG, PNG, WEBP, GIF, PDF, MP4 · Click to browse
                </Typography>
              </Box>
            )}
          </label>
        </Box>

        {/* Upload & Extract Button */}
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!file || uploading}
          fullWidth
          size="large"
          startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <UploadIcon />}
          sx={{
            mt: 2,
            background: 'linear-gradient(135deg, #8B5CF6 0%, #3B82F6 100%)',
            '&:hover': { background: 'linear-gradient(135deg, #7C3AED 0%, #2563EB 100%)' },
            '&:disabled': { opacity: 0.5 },
            borderRadius: 2,
            fontWeight: 600,
            textTransform: 'none',
            fontSize: '1rem',
            py: 1.5,
          }}
        >
          {uploading ? 'Extracting...' : '🔍 Upload & Extract'}
        </Button>
      </FirebaseSelector>

      {/* Processing Status with Agent Steps — same as Tab 1 */}
      <ProcessingStatus
        isProcessing={isProcessingActive}
        currentStep={currentStep}
        steps={processingSteps}
        elapsedTime={elapsedTime}
        estimatedTime={120}
      />

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {/* Analysis Status Summary */}
      {result && (
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Analysis Results</Typography>
            <Chip
              icon={getStatusIcon(result.status || 'pending') || undefined}
              label={(result.status || 'pending').replace('_', ' ').toUpperCase()}
              color={getStatusColor(result.status || 'pending') as any}
              variant="filled"
            />
          </Box>

          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <Card sx={{ textAlign: 'center', bgcolor: alpha('#10B981', 0.1) }}>
                <CardContent sx={{ py: 1.5 }}>
                  <Typography variant="h4" color="success.main">{result.summary?.total_discrepancies === 0 ? '✓' : result.summary?.total_discrepancies}</Typography>
                  <Typography variant="caption">Total Issues</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card sx={{ textAlign: 'center', bgcolor: alpha('#EF4444', 0.1) }}>
                <CardContent sx={{ py: 1.5 }}>
                  <Typography variant="h4" color="error.main">{result.summary?.critical_count || 0}</Typography>
                  <Typography variant="caption">Critical</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card sx={{ textAlign: 'center', bgcolor: alpha('#F59E0B', 0.1) }}>
                <CardContent sx={{ py: 1.5 }}>
                  <Typography variant="h4" color="warning.main">{result.summary?.warning_count || 0}</Typography>
                  <Typography variant="caption">Warnings</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card sx={{ textAlign: 'center', bgcolor: alpha('#3B82F6', 0.1) }}>
                <CardContent sx={{ py: 1.5 }}>
                  <Typography variant="h4" color="info.main">{result.summary?.info_count || 0}</Typography>
                  <Typography variant="caption">Info</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Extracted Data Table — shown for manual upload results */}
      {extractedData && (extractedData.all_fields || extractedData.field_details) && (
        <Paper
          sx={{
            mt: 3,
            p: 3,
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%)',
            border: '1px solid',
            borderColor: 'primary.main',
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              📋 Extracted Data
            </Typography>
            {extractedData.total_fields_extracted && (
              <Chip
                label={`${extractedData.total_fields_extracted} fields`}
                size="small"
                color="primary"
              />
            )}
            {extractedData.detected_language && (
              <Chip
                label={`Language: ${extractedData.detected_language.toUpperCase()}`}
                size="small"
                variant="outlined"
                color="primary"
              />
            )}
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: alpha('#3B82F6', 0.1) }}>
                  <TableCell sx={{ fontWeight: 600 }}>Field</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Language</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Original Text</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>English Text</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Normalized Value</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Confidence</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(extractedData.all_fields || extractedData.field_details || {}).map(([key, fieldValue]) => {
                  const value = fieldValue as any;
                  const displayValue = value?.normalized || value?.translated || value?.english_text || value?.original || (typeof value === 'string' ? value : '');
                  const originalText = value?.original_text || value?.original || (typeof value === 'string' ? value : '');
                  const englishText = value?.english_text || value?.translated || '';
                  const language = value?.detected_language || extractedData.detected_language || 'N/A';
                  return (
                    <TableRow key={key}>
                      <TableCell><strong>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</strong></TableCell>
                      <TableCell>{language}</TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{originalText}</TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{englishText}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{displayValue}</TableCell>
                      <TableCell>
                        <Chip
                          label={value?.confidence ? `${Math.round(value.confidence * 100)}%` : 'N/A'}
                          size="small"
                          color={value?.confidence > 0.8 ? 'success' : value?.confidence > 0.5 ? 'warning' : 'default'}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* 📊 Comparison with ERP Data — LLM-based (same as Tab 1) */}
      {comparisonResult && (
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              📊 Comparison with ERP Data
            </Typography>
            {(() => {
              const withDataResults = comparisonResult.filter(r => r.document_value || r.erp_value);
              const matchCount = withDataResults.filter(r => r.match).length;
              const totalCount = withDataResults.length;
              const matchPct = totalCount > 0 ? Math.round((matchCount / totalCount) * 100) : 0;
              return (
                <>
                  <Chip label={`${matchCount}/${totalCount} fields match`} size="small" color={matchPct === 100 ? 'success' : matchPct >= 50 ? 'warning' : 'error'} sx={{ fontWeight: 600 }} />
                  <Chip label={`${matchPct}% match rate`} size="small" variant="outlined" sx={{ borderColor: matchPct === 100 ? '#10B981' : matchPct >= 50 ? '#F59E0B' : '#EF4444', color: matchPct === 100 ? '#10B981' : matchPct >= 50 ? '#F59E0B' : '#EF4444', fontWeight: 600 }} />
                </>
              );
            })()}
          </Box>
          <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, backgroundColor: 'background.paper', overflow: 'hidden' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: alpha('#8B5CF6', 0.1) }}>
                  <TableCell sx={{ fontWeight: 700, width: '20%' }}>Field</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '25%' }}>📄 Document Value</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '25%' }}>🏢 ERP Value</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '15%', textAlign: 'center' }}>Match Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '15%' }}>Discrepancy</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {comparisonResult.map((cr) => {
                  const bothEmpty = !cr.document_value && !cr.erp_value;
                  const isMatch = cr.match && !bothEmpty;
                  const isNoData = bothEmpty;
                  return (
                    <TableRow key={cr.field} sx={{ backgroundColor: isNoData ? alpha('#6B7280', 0.03) : isMatch ? alpha('#10B981', 0.03) : alpha('#EF4444', 0.05), '&:hover': { backgroundColor: isNoData ? alpha('#6B7280', 0.08) : isMatch ? alpha('#10B981', 0.08) : alpha('#EF4444', 0.1) }, transition: 'background-color 0.2s ease' }}>
                      <TableCell><strong>{cr.field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</strong></TableCell>
                      <TableCell sx={{ color: cr.document_value ? 'text.primary' : 'text.disabled', fontFamily: cr.document_value ? 'monospace' : 'inherit' }}>{cr.document_value || '—  Not found'}</TableCell>
                      <TableCell sx={{ color: cr.erp_value ? 'text.primary' : 'text.disabled', fontFamily: cr.erp_value ? 'monospace' : 'inherit' }}>{cr.erp_value || '—  Not available'}</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        {isNoData ? <Chip label="⚠ No Data" size="small" sx={{ backgroundColor: alpha('#6B7280', 0.15), color: '#9CA3AF', fontWeight: 700, fontSize: '0.75rem' }} />
                          : isMatch ? <Chip label="✓ Match" size="small" sx={{ backgroundColor: alpha('#10B981', 0.15), color: '#10B981', fontWeight: 700, fontSize: '0.75rem' }} />
                          : <Chip label="✗ Mismatch" size="small" sx={{ backgroundColor: alpha('#EF4444', 0.15), color: '#EF4444', fontWeight: 700, fontSize: '0.75rem' }} />}
                      </TableCell>
                      <TableCell>
                        {isMatch ? <Typography variant="caption" sx={{ color: '#10B981', fontWeight: 700, fontSize: '1rem' }}>✓</Typography>
                          : isNoData ? <Typography variant="caption" color="text.disabled">—</Typography>
                          : cr.discrepancy_type ? <Chip label={cr.discrepancy_type.replace(/_/g, ' ')} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 22, borderColor: alpha('#F59E0B', 0.6), color: '#F59E0B' }} />
                          : <Typography variant="caption" color="text.disabled">—</Typography>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Discrepancies Detail */}
      {discrepancies.length > 0 && discrepancies.some((d: any) => !d.match && d.severity !== 'info') && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            Discrepancies Requiring Attention
          </Typography>
          {discrepancies.filter((d: any) => d.severity !== 'info' || d.description !== 'Values match').map((disc: any, idx: number) => (
            <Alert
              key={idx}
              severity={disc.severity === 'critical' ? 'error' : disc.severity === 'warning' ? 'warning' : 'info'}
              sx={{ mt: 1 }}
            >
              <Typography variant="body2">
                <strong>{disc.field?.replace(/_/g, ' ')}:</strong> {disc.description || 'Discrepancy detected'}
              </Typography>
              {(disc.registered_value || disc.extracted_value) && (
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  ERP: "{disc.registered_value || 'N/A'}" → Extracted: "{disc.extracted_value || 'N/A'}"
                </Typography>
              )}
            </Alert>
          ))}
        </Box>
      )}

      {/* No Discrepancies Message */}
      {result && discrepancies.filter((d: any) => d.severity !== 'info').length === 0 && (
        <Alert severity="success" sx={{ mt: 3 }}>
          <Typography variant="body2">
            <strong>All Clear!</strong> No discrepancies found between extracted data and ERP registration.
          </Typography>
        </Alert>
      )}
    </Box>
  );
}

export default VehicleUpload;

